// A product's recipes at this venue — the product dialog's **Stock used** tab (spec/13 §6.1, §6.4; ADR-0040 §3;
// read with `inventory:read`, written with `inventory:manage`; shown only where stock is on here). What one unit takes
// from stock: the product's **base** recipe, one for each **size** (a size with none uses the base) and one for each
// **extra**, added per unit of the line it is chosen on ("Extra cheese": 20 g of cheese). Each line is a stock item
// and a quantity in its natural unit.
//
// With `inventory:reports`, each recipe shows what it costs at the items' average costs and the margin on its price
// before tax — the product's, a size's (a size with no recipe of its own earns on the base recipe) or an extra's own —
// as the server answers them with the recipes (the maths is the server's, exact, rounded once). A recipe whose items
// were never costed says so. Recipes are this venue's own: the organisation's other venues keep theirs. Saving replaces
// the product's recipes here as a whole.

import type { MenuProduct, StockItem, StockProductRecipes, StockRecipe, StockSellableUnit } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Input, Select, useToast } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { LoadFailure } from '../../shell/LoadFailure';
import { percentOf } from '../reports/tab-kit';
import { Icon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { Notice, TableSkeleton } from '../../ui';
import { useStockAccess } from './access';
import { NO_FIGURE, useStockFormat } from './format';
import { ItemPicker } from './parts';
import { useProductRecipes, usePutProductRecipes, useStockItems, useStockStatus } from './queries';
import { inputTextOf, inputUnitsFor, naturalInputUnit, parseQuantity, type DisplayUnit } from './units';

import './stock.css';
import { useProblem } from '../../api/use-problem';

/** Is stock tracked here, and may this member see recipes? The product dialog shows its Stock used tab only then. */
export function useRecipesShown(): boolean {
  const venue = useCurrentVenue();
  const access = useStockAccess(venue.id);
  const status = useStockStatus(venue.organizationId, venue.id, access.read);
  return access.read && status.data?.enabled === true;
}

interface Target {
  /** `base`, `v:<variantId>` or `m:<modifierId>`. */
  readonly key: string;
  readonly variantId: string | null;
  readonly modifierId: string | null;
  readonly title: string;
  readonly subtitle: string;
}

type RecipeDrafts = Record<string, ComponentDraft[]>;

/** What a recipe, or a thing a player buys, costs and earns: the recipe's own figures, or a size's. */
type Earning = Pick<StockSellableUnit, 'cost' | 'costComplete' | 'price' | 'priceExclTax' | 'margin' | 'marginBps'>;

interface ComponentDraft {
  readonly key: string;
  readonly stockItemId: string;
  readonly amount: string;
  readonly unit: DisplayUnit;
}

let componentKey = 0;
const nextKey = () => `component-${(componentKey += 1)}`;

const targetKeyOf = (recipe: Pick<StockRecipe, 'variantId' | 'modifierId'>) => (recipe.variantId !== null ? `v:${recipe.variantId}` : recipe.modifierId !== null ? `m:${recipe.modifierId}` : 'base');

function draftsOf(recipes: StockProductRecipes, items: ReadonlyMap<string, StockItem>): Record<string, ComponentDraft[]> {
  const drafts: Record<string, ComponentDraft[]> = {};
  for (const recipe of recipes.recipes) {
    drafts[targetKeyOf(recipe)] = recipe.components.map((component) => {
      const unit = naturalInputUnit(component.baseUnit, [component.quantity, ...(items.get(component.stockItemId)?.packs.map((pack) => pack.quantity) ?? [])].filter((value) => value < 100_000));
      const shown = component.baseUnit === 'each' || component.quantity >= 1000 ? unit : component.baseUnit;
      return { key: nextKey(), stockItemId: component.stockItemId, amount: inputTextOf(component.quantity, shown), unit: shown };
    });
  }
  return drafts;
}

export function RecipesTab({ product, onDone }: { readonly product: MenuProduct; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const access = useStockAccess(venue.id);
  const recipes = useProductRecipes(venue.organizationId, venue.id, product.id);
  const items = useStockItems(venue.organizationId, venue.id);

  if (recipes.data === undefined || items.data === undefined) {
    const failed = recipes.isError ? recipes.error : items.isError ? items.error : null;
    return failed === null ? <TableSkeleton rows={4} label={t('stock.loading')} /> : <LoadFailure error={failed} message={t('stock.recipes.unavailable')} onRetry={() => void recipes.refetch()} />;
  }
  return (
    // Started once from what was read: a refetch (the window refocused) must not throw away what is being typed.
    <RecipesForm
      product={product}
      recipes={recipes.data}
      items={items.data}
      canEdit={access.manage}
      reports={access.reports}
      onDone={onDone}
    />
  );
}

function RecipesForm({
  product,
  recipes,
  items,
  canEdit,
  reports,
  onDone,
}: {
  readonly product: MenuProduct;
  readonly recipes: StockProductRecipes;
  readonly items: readonly StockItem[];
  readonly canEdit: boolean;
  readonly reports: boolean;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const venue = useCurrentVenue();
  const save = usePutProductRecipes(venue.organizationId, venue.id, product.id);
  const byId = new Map(items.map((item) => [item.id, item]));
  const [drafts, setDrafts] = useState<RecipeDrafts>(() => draftsOf(recipes, byId));
  const [initial] = useState(() => JSON.stringify(draftsOf(recipes, byId), (key, value: unknown) => (key === 'key' ? undefined : value)));
  const [adding, setAdding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<ReadonlySet<string>>(new Set());

  const targets: Target[] = [
    { key: 'base', variantId: null, modifierId: null, title: product.variants.length === 0 ? product.name : t('stock.recipes.base'), subtitle: product.variants.length === 0 ? t('stock.recipes.perUnit') : t('stock.recipes.baseHint') },
    ...product.variants.map((variant) => ({ key: `v:${variant.id}`, variantId: variant.id, modifierId: null, title: variant.name, subtitle: t('stock.recipes.size') })),
    ...product.modifierGroups.flatMap((group) => group.modifiers.map((modifier) => ({ key: `m:${modifier.id}`, variantId: null, modifierId: modifier.id, title: modifier.name, subtitle: t('stock.recipes.extra', { group: group.name }) }))),
  ];
  const saved = new Map(recipes.recipes.map((recipe) => [targetKeyOf(recipe), recipe]));
  const changed = JSON.stringify(drafts, (key, value: unknown) => (key === 'key' ? undefined : value)) !== initial;
  const changedTarget = (key: string) => {
    const before = draftsOf(recipes, byId)[key] ?? [];
    const now = drafts[key] ?? [];
    return JSON.stringify(before.map((one) => [one.stockItemId, parseQuantity(one.amount, one.unit)])) !== JSON.stringify(now.map((one) => [one.stockItemId, parseQuantity(one.amount, one.unit)]));
  };
  const setTarget = (key: string, next: ComponentDraft[]) => setDrafts((current) => ({ ...current, [key]: next }));

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!changed) {
      setError(t('stock.editor.noChanges'));
      return;
    }
    const bad = new Set<string>();
    const body = targets
      .map((target) => {
        const components = (drafts[target.key] ?? []).map((component) => {
          const quantity = parseQuantity(component.amount, component.unit);
          if (quantity === null || quantity < 1) bad.add(component.key);
          return { stockItemId: component.stockItemId, quantity: quantity ?? 0 };
        });
        return { target, components };
      })
      .filter((entry) => entry.components.length > 0)
      .map(({ target, components }) => ({ ...(target.variantId === null ? {} : { variantId: target.variantId }), ...(target.modifierId === null ? {} : { modifierId: target.modifierId }), components }));
    setInvalid(bad);
    if (bad.size > 0) {
      setError(t('stock.recipes.invalid'));
      return;
    }
    setError(null);
    save.mutate(
      { recipes: body },
      {
        onSuccess: () => {
          show({ tone: 'ok', title: t('stock.recipes.saved', { name: product.name }) });
          onDone();
        },
        onError: (failure) => setError(problem.text(failure, { fallback: t('stock.recipes.refused') })),
      },
    );
  }

  return (
    <form className="console-form console-stock-recipes" aria-label={t('stock.recipes.title')} noValidate onSubmit={submit}>
      <p className="console-muted">{t('stock.recipes.intro', { venue: venue.name })}</p>
      {recipes.soldOutForStock ? (
        <Notice tone="warn" title={t('stock.recipes.soldOutTitle')}>
          <p>{t('stock.recipes.soldOutBody')}</p>
        </Notice>
      ) : null}
      {recipes.retailItemId !== null && recipes.recipes.length === 0 ? (
        <Notice role="note">
          <p>{t('stock.recipes.retail', { name: byId.get(recipes.retailItemId)?.name ?? product.name })}</p>
        </Notice>
      ) : null}
      {error !== null ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      {targets.map((target) => {
        const components = drafts[target.key] ?? [];
        const recipe = saved.get(target.key);
        // A size earns on its own recipe, or the base one: what the server answers for it as a thing a player buys.
        const unit = target.variantId === null ? undefined : recipes.units.find((one) => one.variantId === target.variantId);
        const earning: Earning | undefined = target.variantId === null ? recipe : unit?.tracked === true ? unit : undefined;
        return (
          <section key={target.key} className="console-stock-recipe" aria-labelledby={`recipe-${target.key}`}>
            <header className="console-stock-recipe__head">
              <div>
                <h3 id={`recipe-${target.key}`} className="console-stock-recipe__title">
                  {target.title}
                </h3>
                <p className="console-subtle">{target.subtitle}</p>
              </div>
              {recipe === undefined || changedTarget(target.key) ? null : recipe.makeable ? (
                <Badge tone="ok">{t('stock.recipes.makeable')}</Badge>
              ) : (
                <Badge tone="danger" dot>
                  {t('stock.recipes.notMakeable')}
                </Badge>
              )}
            </header>
            {components.length === 0 ? (
              <p className="console-subtle">{target.variantId !== null && (drafts['base'] ?? []).length > 0 ? t('stock.recipes.usesBase') : t('stock.recipes.none')}</p>
            ) : (
              <ul className="console-stock-recipe__list">
                {components.map((component) => {
                  const item = byId.get(component.stockItemId);
                  const savedComponent = recipe?.components.find((one) => one.stockItemId === component.stockItemId);
                  return (
                    <ComponentRow
                      key={component.key}
                      component={component}
                      item={item}
                      cost={reports && !changedTarget(target.key) ? (savedComponent?.cost ?? null) : undefined}
                      canEdit={canEdit}
                      invalid={invalid.has(component.key)}
                      onChange={(next) => setTarget(target.key, components.map((one) => (one.key === component.key ? next : one)))}
                      onRemove={() => setTarget(target.key, components.filter((one) => one.key !== component.key))}
                    />
                  );
                })}
              </ul>
            )}
            {canEdit ? (
              adding === target.key ? (
                <div className="console-stock-recipe__add">
                  <ItemPicker
                    organizationId={venue.organizationId}
                    venueId={venue.id}
                    items={items}
                    label={t('stock.recipes.pick', { name: target.title })}
                    exclude={new Set(components.map((one) => one.stockItemId))}
                    autoFocus
                    onPick={(item) => {
                      const unit = naturalInputUnit(item.baseUnit, []);
                      setTarget(target.key, [...components, { key: nextKey(), stockItemId: item.id, amount: '', unit }]);
                      setAdding(null);
                    }}
                  />
                  <Button type="button" variant="ghost" size="sm" onClick={() => setAdding(null)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              ) : (
                <div>
                  <Button type="button" variant="ghost" size="sm" icon={<Icon name="plus" />} onClick={() => setAdding(target.key)}>
                    {t('stock.recipes.add')}
                  </Button>
                </div>
              )
            ) : null}
            {reports && (components.length > 0 || earning !== undefined) ? (
              <CostLine earning={earning} changed={changedTarget(target.key) || (components.length === 0 && changedTarget('base'))} extra={target.modifierId !== null} />
            ) : null}
          </section>
        );
      })}
      {canEdit ? (
        <div className="console-form__actions console-form__actions--end">
          <Button type="button" variant="ghost" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" busy={save.isPending}>
            {t('stock.recipes.save')}
          </Button>
        </div>
      ) : (
        <p className="console-subtle">{t('stock.recipes.readOnly')}</p>
      )}
    </form>
  );
}

/** One line of a recipe: the item, how much of it in its natural unit, and — saved — what it costs. */
function ComponentRow({
  component,
  item,
  cost,
  canEdit,
  invalid,
  onChange,
  onRemove,
}: {
  readonly component: ComponentDraft;
  readonly item: StockItem | undefined;
  /** The saved component's cost; `undefined` when it is not shown (no reports, or the recipe has changed). */
  readonly cost: StockRecipe['components'][number]['cost'] | undefined;
  readonly canEdit: boolean;
  readonly invalid: boolean;
  readonly onChange: (next: ComponentDraft) => void;
  readonly onRemove: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const stock = useStockFormat();
  const name = item?.name ?? t('stock.recipes.unknownItem');
  const units = item === undefined ? [component.unit] : inputUnitsFor(item.baseUnit);
  const quantity = parseQuantity(component.amount, component.unit);
  return (
    <li className="console-stock-recipe__row">
      <span className="console-stock-recipe__item">{name}</span>
      {canEdit ? (
        <span className="console-stock-qty__loose">
          <Input
            numeric
            className="console-stock-qty__input"
            aria-label={t('stock.recipes.quantityOf', { name })}
            aria-invalid={invalid || undefined}
            value={component.amount}
            placeholder="0"
            onChange={(event) => onChange({ ...component, amount: event.target.value })}
          />
          {units.length > 1 ? (
            <Select className="console-stock-qty__unit" aria-label={t('stock.quantity.unitOf', { name })} value={component.unit} onValueChange={(value) => onChange({ ...component, unit: value as DisplayUnit })} options={units.map((one) => ({ value: one, label: stock.unitName(one, item) }))} />
          ) : (
            <span className="console-stock-qty__unit-word">{stock.unitName(component.unit, item)}</span>
          )}
        </span>
      ) : (
        <span className="console-num">{quantity === null || item === undefined ? component.amount : stock.quantity(quantity, item)}</span>
      )}
      <span className="console-num console-muted console-stock-recipe__cost">{cost === undefined ? '' : cost === null ? t('stock.recipes.noCost') : stock.money(cost)}</span>
      {canEdit ? <Button type="button" variant="ghost" size="sm" icon={<Icon name="close" />} label={t('stock.recipes.remove', { name })} onClick={onRemove} /> : null}
    </li>
  );
}

/** What the recipe costs and its margin on its price before tax — for an extra, on the extra's own price. */
function CostLine({ earning, changed, extra }: { readonly earning: Earning | undefined; readonly changed: boolean; readonly extra: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const stock = useStockFormat();
  const format = useVenueFormatter();
  if (changed || earning === undefined) return <p className="console-stock-recipe__foot console-subtle">{t('stock.recipes.costOnSave')}</p>;
  return (
    <p className="console-stock-recipe__foot">
      <span>
        {t(extra ? 'stock.recipes.costPerExtra' : 'stock.recipes.cost')} <span className="console-money console-stock-recipe__figure">{stock.money(earning.cost)}</span>
        {earning.costComplete ? null : <span className="console-subtle"> {t('stock.recipes.costIncomplete')}</span>}
      </span>
      {earning.margin === null ? null : (
        <span>
          {t('stock.recipes.margin')} <span className="console-money console-stock-recipe__figure">{stock.money(earning.margin)}</span>{' '}
          <span className="console-num console-muted">{earning.marginBps === null ? NO_FIGURE : t('stock.recipes.marginPercent', { percent: percentOf(format, earning.marginBps, 1) })}</span>
          <span className="console-subtle"> {t('stock.recipes.onPrice', { price: stock.money(earning.priceExclTax) })}</span>
        </span>
      )}
    </p>
  );
}
